/**
 * レジ締め・精算管理コンポーネント
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  PlayArrow as OpenIcon,
  Stop as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Receipt as ReportIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import api from "../../services/api";

// 日本円の金種
const DENOMINATIONS = [
  { value: 10000, name: "1万円札" },
  { value: 5000, name: "5千円札" },
  { value: 2000, name: "2千円札" },
  { value: 1000, name: "千円札" },
  { value: 500, name: "500円玉" },
  { value: 100, name: "100円玉" },
  { value: 50, name: "50円玉" },
  { value: 10, name: "10円玉" },
  { value: 5, name: "5円玉" },
  { value: 1, name: "1円玉" },
];

const CashDrawerManager = () => {
  const { storeId } = useParams();
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  // ダイアログ状態
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [cashInDialog, setCashInDialog] = useState(false);
  const [cashOutDialog, setCashOutDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);

  // フォーム状態
  const [openingCash, setOpeningCash] = useState({});
  const [closingCash, setClosingCash] = useState({});
  const [cashAmount, setCashAmount] = useState("");
  const [cashReason, setCashReason] = useState("");
  // M-5 FIX: 過不足時の説明入力用
  const [discrepancyNote, setDiscrepancyNote] = useState("");

  // 履歴・レポート
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionReport, setSessionReport] = useState(null);

  // スナックバー
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCurrentSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchCurrentSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cash-drawer/${storeId}/current`);
      setCurrentSession(response.data.session);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError("セッション情報の取得に失敗しました");
      }
      setCurrentSession(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateDenominationTotal = (denominations) => {
    return Object.entries(denominations).reduce((total, [denom, count]) => {
      return total + (parseInt(denom) * (parseInt(count) || 0));
    }, 0);
  };

  const handleOpenSession = async () => {
    try {
      const openingAmount = calculateDenominationTotal(openingCash);
      const denominationBreakdown = Object.entries(openingCash)
        .filter(([, count]) => count > 0)
        .map(([denom, count]) => ({
          denomination: parseInt(denom),
          count: parseInt(count),
          subtotal: parseInt(denom) * parseInt(count),
        }));

      // NEW-CRIT-001 FIX: バックエンドAPIフィールド名に合わせる
      const response = await api.post(`/cash-drawer/${storeId}/open`, {
        openingAmount: openingAmount,
        denominations: denominationBreakdown,
      });

      setCurrentSession(response.data.session);
      setOpenDialog(false);
      setOpeningCash({});
      setSnackbar({
        open: true,
        message: "レジ開設しました",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "レジ開設に失敗しました",
        severity: "error",
      });
    }
  };

  const handleCloseSession = async () => {
    try {
      const closingAmount = calculateDenominationTotal(closingCash);
      // NEW-CRIT-002 FIX: バックエンドAPIフィールド名に対応
      // expectedAmount（理論在高）= 開始金額 + 現金売上 - 返金 + 入金合計 - 出金合計
      const openingAmt = currentSession?.openingAmount || currentSession?.opening_amount || 0;
      const cashSales = currentSession?.cashSalesTotal || currentSession?.cash_sales_total || 0;
      const refundTotal = currentSession?.refundTotal || currentSession?.refund_total || 0;
      const cashInArr = currentSession?.cashIn || currentSession?.cash_in || [];
      const cashOutArr = currentSession?.cashOut || currentSession?.cash_out || [];
      const cashInTotal = Array.isArray(cashInArr) ? cashInArr.reduce((sum, item) => sum + (item.amount || 0), 0) : 0;
      const cashOutTotal = Array.isArray(cashOutArr) ? cashOutArr.reduce((sum, item) => sum + (item.amount || 0), 0) : 0;
      const expectedCash = openingAmt + cashSales - refundTotal + cashInTotal - cashOutTotal;
      const hasDiscrepancy = closingAmount !== expectedCash;

      // M-5 FIX: 過不足がある場合は説明必須
      if (hasDiscrepancy && !discrepancyNote.trim()) {
        setSnackbar({
          open: true,
          message: "過不足がある場合は説明を入力してください",
          severity: "warning",
        });
        return;
      }

      const denominationBreakdown = Object.entries(closingCash)
        .filter(([, count]) => count > 0)
        .map(([denom, count]) => ({
          denomination: parseInt(denom),
          count: parseInt(count),
          subtotal: parseInt(denom) * parseInt(count),
        }));

      // NEW-CRIT-001 FIX: バックエンドAPIフィールド名に合わせる
      const response = await api.post(`/cash-drawer/${storeId}/close`, {
        closingAmount: closingAmount,
        denominations: denominationBreakdown,
        // M-5 FIX: 過不足の説明を送信
        discrepancyNote: hasDiscrepancy ? discrepancyNote : undefined,
      });

      // NEW-CRIT-002 FIX: バックエンドはsummaryを返す（reportではない）
      setSessionReport(response.data.summary || response.data.report);
      setCurrentSession(null);
      setCloseDialog(false);
      setReportDialog(true);
      setClosingCash({});
      setDiscrepancyNote("");
      setSnackbar({
        open: true,
        message: "レジ締めが完了しました",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "レジ締めに失敗しました",
        severity: "error",
      });
    }
  };

  const handleCashIn = async () => {
    try {
      const amount = parseInt(cashAmount);
      if (!amount || amount <= 0) {
        setSnackbar({
          open: true,
          message: "金額を入力してください",
          severity: "error",
        });
        return;
      }

      await api.post(`/cash-drawer/${storeId}/cash-in`, {
        amount,
        reason: cashReason || "入金",
      });

      await fetchCurrentSession();
      setCashInDialog(false);
      setCashAmount("");
      setCashReason("");
      setSnackbar({
        open: true,
        message: `¥${amount.toLocaleString()} を入金しました`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "入金に失敗しました",
        severity: "error",
      });
    }
  };

  const handleCashOut = async () => {
    try {
      const amount = parseInt(cashAmount);
      if (!amount || amount <= 0) {
        setSnackbar({
          open: true,
          message: "金額を入力してください",
          severity: "error",
        });
        return;
      }

      await api.post(`/cash-drawer/${storeId}/cash-out`, {
        amount,
        reason: cashReason || "出金",
      });

      await fetchCurrentSession();
      setCashOutDialog(false);
      setCashAmount("");
      setCashReason("");
      setSnackbar({
        open: true,
        message: `¥${amount.toLocaleString()} を出金しました`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "出金に失敗しました",
        severity: "error",
      });
    }
  };

  const fetchSessionHistory = async () => {
    try {
      const response = await api.get(`/cash-drawer/${storeId}/history`);
      setSessionHistory(response.data.sessions || []);
      setHistoryDialog(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "履歴の取得に失敗しました",
        severity: "error",
      });
    }
  };

  const fetchSessionReport = async (sessionId) => {
    try {
      const response = await api.get(`/cash-drawer/${storeId}/report/${sessionId}`);
      setSessionReport(response.data.report);
      setHistoryDialog(false);
      setReportDialog(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "レポートの取得に失敗しました",
        severity: "error",
      });
    }
  };

  const DenominationInput = ({ value, onChange }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>金種</TableCell>
            <TableCell align="center">枚数</TableCell>
            <TableCell align="right">小計</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {DENOMINATIONS.map((denom) => (
            <TableRow key={denom.value}>
              <TableCell>{denom.name}</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={value[denom.value] || ""}
                  onChange={(e) => onChange({
                    ...value,
                    [denom.value]: Math.max(0, parseInt(e.target.value) || 0),
                  })}
                  inputProps={{ min: 0, style: { textAlign: "center" } }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="right">
                ¥{((value[denom.value] || 0) * denom.value).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2}><strong>合計</strong></TableCell>
            <TableCell align="right">
              <strong>¥{calculateDenominationTotal(value).toLocaleString()}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">レジ管理</Typography>
          <Box>
            <IconButton onClick={fetchCurrentSession} title="更新">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={fetchSessionHistory} title="履歴">
              <HistoryIcon />
            </IconButton>
          </Box>
        </Box>

        {currentSession ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              レジ営業中
            </Alert>

            <Grid container spacing={2}>
              {/* NEW-CRIT-002 FIX: バックエンドAPIフィールド名に対応 */}
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "grey.100" }}>
                  <Typography variant="body2" color="textSecondary">開始時準備金</Typography>
                  <Typography variant="h6">
                    ¥{(currentSession.openingAmount || currentSession.opening_amount || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "success.light" }}>
                  <Typography variant="body2" color="textSecondary">現金売上</Typography>
                  <Typography variant="h6">
                    ¥{(currentSession.cashSalesTotal || currentSession.cash_sales_total || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.light" }}>
                  <Typography variant="body2" color="textSecondary">入出金</Typography>
                  <Typography variant="h6">
                    ¥{(() => {
                      const cashInArr = currentSession.cashIn || currentSession.cash_in || [];
                      const cashOutArr = currentSession.cashOut || currentSession.cash_out || [];
                      const inTotal = Array.isArray(cashInArr) ? cashInArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
                      const outTotal = Array.isArray(cashOutArr) ? cashOutArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
                      return (inTotal - outTotal).toLocaleString();
                    })()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.light" }}>
                  <Typography variant="body2" color="textSecondary">理論在高</Typography>
                  <Typography variant="h6">
                    ¥{(() => {
                      const openingAmt = currentSession.openingAmount || currentSession.opening_amount || 0;
                      const cashSales = currentSession.cashSalesTotal || currentSession.cash_sales_total || 0;
                      const refundTotal = currentSession.refundTotal || currentSession.refund_total || 0;
                      const cashInArr = currentSession.cashIn || currentSession.cash_in || [];
                      const cashOutArr = currentSession.cashOut || currentSession.cash_out || [];
                      const inTotal = Array.isArray(cashInArr) ? cashInArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
                      const outTotal = Array.isArray(cashOutArr) ? cashOutArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
                      return (openingAmt + cashSales - refundTotal + inTotal - outTotal).toLocaleString();
                    })()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => setCashInDialog(true)}
              >
                入金
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RemoveIcon />}
                onClick={() => setCashOutDialog(true)}
              >
                出金
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => setCloseDialog(true)}
              >
                レジ締め
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              レジが開設されていません
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<OpenIcon />}
              onClick={() => setOpenDialog(true)}
            >
              レジ開設
            </Button>
          </Box>
        )}
      </Paper>

      {/* レジ開設ダイアログ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>レジ開設</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            開始時の準備金を金種別に入力してください
          </Typography>
          <DenominationInput value={openingCash} onChange={setOpeningCash} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleOpenSession}>
            開設
          </Button>
        </DialogActions>
      </Dialog>

      {/* レジ締めダイアログ */}
      <Dialog open={closeDialog} onClose={() => setCloseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>レジ締め</DialogTitle>
        <DialogContent>
          {/* NEW-CRIT-002 FIX: 理論在高を動的に計算 */}
          {(() => {
            const openingAmt = currentSession?.openingAmount || currentSession?.opening_amount || 0;
            const cashSales = currentSession?.cashSalesTotal || currentSession?.cash_sales_total || 0;
            const refundTotal = currentSession?.refundTotal || currentSession?.refund_total || 0;
            const cashInArr = currentSession?.cashIn || currentSession?.cash_in || [];
            const cashOutArr = currentSession?.cashOut || currentSession?.cash_out || [];
            const inTotal = Array.isArray(cashInArr) ? cashInArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
            const outTotal = Array.isArray(cashOutArr) ? cashOutArr.reduce((s, i) => s + (i.amount || 0), 0) : 0;
            const expectedCash = openingAmt + cashSales - refundTotal + inTotal - outTotal;
            const actualCash = calculateDenominationTotal(closingCash);
            const discrepancy = actualCash - expectedCash;

            return (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  理論在高: ¥{expectedCash.toLocaleString()}
                </Alert>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  実際の在高を金種別に入力してください
                </Typography>
                <DenominationInput value={closingCash} onChange={setClosingCash} />
                {/* M-3 FIX: 過不足のUI表示改善 */}
                {actualCash !== expectedCash && (
                  <>
                    <Alert
                      severity={discrepancy > 0 ? "info" : "error"}
                      sx={{ mt: 2 }}
                    >
                      差額: ¥{discrepancy.toLocaleString()}
                      {discrepancy > 0
                        ? " (過剰 - 入金漏れの可能性があります)"
                        : " (不足 - 出金漏れまたは誤差があります)"}
                    </Alert>
                    {/* M-5 FIX: 過不足時の説明入力必須 */}
                    <TextField
                      fullWidth
                      required
                      label="過不足の理由（必須）"
                      value={discrepancyNote}
                      onChange={(e) => setDiscrepancyNote(e.target.value)}
                      placeholder="例: 釣銭渡し間違い、入金記録漏れ等"
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                      error={!discrepancyNote.trim()}
                      helperText={!discrepancyNote.trim() ? "過不足がある場合は理由を入力してください" : ""}
                    />
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCloseDialog(false);
            setClosingCash({});
            setDiscrepancyNote("");
          }}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleCloseSession}>
            締め処理実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 入金ダイアログ */}
      <Dialog open={cashInDialog} onClose={() => setCashInDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>入金</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="金額"
            type="number"
            value={cashAmount}
            onChange={(e) => setCashAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">¥</InputAdornment>,
            }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="理由（任意）"
            value={cashReason}
            onChange={(e) => setCashReason(e.target.value)}
            placeholder="例: 両替金追加"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashInDialog(false)}>キャンセル</Button>
          <Button variant="contained" color="success" onClick={handleCashIn}>
            入金
          </Button>
        </DialogActions>
      </Dialog>

      {/* 出金ダイアログ */}
      <Dialog open={cashOutDialog} onClose={() => setCashOutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>出金</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="金額"
            type="number"
            value={cashAmount}
            onChange={(e) => setCashAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">¥</InputAdornment>,
            }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="理由（任意）"
            value={cashReason}
            onChange={(e) => setCashReason(e.target.value)}
            placeholder="例: 銀行入金"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashOutDialog(false)}>キャンセル</Button>
          <Button variant="contained" color="warning" onClick={handleCashOut}>
            出金
          </Button>
        </DialogActions>
      </Dialog>

      {/* 履歴ダイアログ */}
      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>精算履歴</DialogTitle>
        <DialogContent>
          {sessionHistory.length === 0 ? (
            <Typography color="textSecondary">履歴がありません</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>日時</TableCell>
                    <TableCell align="right">現金売上</TableCell>
                    <TableCell align="right">差額</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionHistory.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        {new Date(session.openedAt).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell align="right">
                        ¥{(session.cashSales || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`¥${(session.discrepancy || 0).toLocaleString()}`}
                          color={session.discrepancy === 0 ? "success" : session.discrepancy > 0 ? "info" : "error"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={session.status === "CLOSED" ? "締め済" : "営業中"}
                          color={session.status === "CLOSED" ? "default" : "success"}
                        />
                      </TableCell>
                      <TableCell>
                        {session.status === "CLOSED" && (
                          <Button
                            size="small"
                            startIcon={<ReportIcon />}
                            onClick={() => fetchSessionReport(session.id)}
                          >
                            レポート
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* レポートダイアログ */}
      <Dialog open={reportDialog} onClose={() => setReportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>精算レポート</DialogTitle>
        <DialogContent>
          {sessionReport && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">開始時刻</Typography>
                  <Typography>{new Date(sessionReport.openedAt).toLocaleString("ja-JP")}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">終了時刻</Typography>
                  <Typography>{new Date(sessionReport.closedAt).toLocaleString("ja-JP")}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* NEW-CRIT-002 FIX: バックエンドAPIフィールド名に対応 */}
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>開始時準備金</TableCell>
                      <TableCell align="right">¥{(sessionReport.openingAmount || sessionReport.openingCash || 0).toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>現金売上</TableCell>
                      <TableCell align="right">¥{(sessionReport.cashSalesTotal || sessionReport.cashSales || 0).toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>入金</TableCell>
                      <TableCell align="right">¥{(sessionReport.cashInTotal || sessionReport.cashIn || 0).toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>出金</TableCell>
                      <TableCell align="right">¥{(sessionReport.cashOutTotal || sessionReport.cashOut || 0).toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell><strong>理論在高</strong></TableCell>
                      <TableCell align="right"><strong>¥{(sessionReport.expectedAmount || sessionReport.expectedCash || 0).toLocaleString()}</strong></TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell><strong>実際在高</strong></TableCell>
                      <TableCell align="right"><strong>¥{(sessionReport.closingAmount || sessionReport.actualCash || 0).toLocaleString()}</strong></TableCell>
                    </TableRow>
                    {/* M-3 FIX: 過不足のUI表示改善 */}
                    <TableRow sx={{
                      bgcolor: sessionReport.discrepancy === 0
                        ? "success.light"
                        : sessionReport.discrepancy > 0
                          ? "info.light"
                          : "error.light"
                    }}>
                      <TableCell><strong>差額</strong></TableCell>
                      <TableCell align="right">
                        <strong>¥{(sessionReport.discrepancy || 0).toLocaleString()}</strong>
                        {sessionReport.discrepancy !== 0 && (
                          sessionReport.discrepancy > 0 ? " (過剰)" : " (不足)"
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>売上内訳</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">取引件数</Typography>
                  <Typography>{sessionReport.transactionCount || 0}件</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">合計売上</Typography>
                  <Typography>¥{(sessionReport.totalSales || 0).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashDrawerManager;
